/**
 * Types for Protocol B - Traffic Regulator Communications
 * Based on: "Protocolo de Comunicaciones del Regulador de Tráfico de Barcelona"
 */

// =============================================================================
// NETWORK HIERARCHY
// =============================================================================

export enum NodeLevel {
  HOST = 0,        // Ordenador (bits 14-15, values 0-2)
  CENTRAL = 1,     // Central (bits 8-13, values 1-62)
  REGULATOR = 2,   // Regulador (bits 0-5, values 1-62)
  SUBREGULATOR = 3 // Subregulador (bits 7-8, values 1-2)
}

export interface PhysicalId {
  host: number;        // 0-2
  central: number;     // 1-62 (0 = not applicable)
  regulator: number;   // 1-62 (0 = not applicable)
  subregulator: number; // 1-2 (0 = not applicable)
}

export interface NodeIdentification {
  physical: PhysicalId;
  logical: number;     // 1-32767
  level: NodeLevel;
  nodeType: NodeType;
  version: number;     // Julian date since 1980-01-01
}

export enum NodeType {
  UNKNOWN = 0,
  TRAFFIC_REGULATOR = 20,
  RESERVED = 80
}

// =============================================================================
// COMMUNICATION CHANNELS
// =============================================================================

export enum Channel {
  CANAL_CONS = 0,  // Local terminal
  CANAL_ICOM = 1,  // Command interpreter
  CANAL_GCOM = 2,  // Communications manager
  CANAL_DET = 3,   // Detector management
  CANAL_ALAR = 4   // Alarm management
}

// =============================================================================
// CONTROL CODES
// =============================================================================

export const ControlCodes = {
  TOK: 0x01,   // Test OK - communication successful
  STX: 0x02,   // Start of frame
  ETX: 0x03,
  EOT: 0x04,
  TML: 0x06,   // Test Error - no response
  TRT: 0x07,   // Test Response
  CR: 0x0D,
  DLE: 0x10,   // Escape for reserved bytes
  XON: 0x11,
  XOFF: 0x13,
  NAK: 0x15,   // Negative acknowledge
  ETB: 0x17,
  EOB: 0x18,
  EOF: 0x1A,   // End of frame
  ESC: 0x1B,
  ACK0: 0x30,  // Acknowledge even
  ACK1: 0x31   // Acknowledge odd
} as const;

// =============================================================================
// LINK STATE
// =============================================================================

export enum LinkState {
  UNKNOWN = 'unknown',
  OK = 'ok',
  ERROR = 'error',
  COUNTING_ERRORS = 'counting_errors',
  COUNTING_OK = 'counting_ok'
}

export interface Link {
  id: string;
  sourceNode: string;
  targetNode: string;
  state: LinkState;
  testCount: number;
  lastTestTime: number;
}

// =============================================================================
// FRAMES
// =============================================================================

export enum FrameType {
  TEST_COMMUNICATION = 'test',
  IDENTIFICATION = 'identification',
  TEST_RESPONSE = 'test_response',
  MAINTENANCE = 'maintenance',
  INFORMATION = 'information',
  FLOW_CONTROL = 'flow_control',
  ERROR = 'error'
}

export interface Frame {
  type: FrameType;
  raw: Uint8Array;
  decoded: DecodedFrame;
  timestamp: number;
  direction: 'tx' | 'rx';
}

export interface DecodedFrame {
  stx?: number;
  data: Uint8Array;
  crc: number;
  crcValid: boolean;
  eof?: number;
}

export interface RoutedFrameHeader {
  destinationChannel: number;
  isResponse: boolean;
  isRouted: boolean;
  isMaintenance: boolean;
  sourceChannel: number;
  expectsResponse: boolean;
  isContinuation: boolean;
  hasError: boolean;
  destinationPhysical: PhysicalId;
  sourcePhysical: PhysicalId;
  counter: number;
}

// =============================================================================
// REGULATOR STATES
// =============================================================================

export enum RegulatorState {
  DISCONNECTED = 0,    // Desconectado
  OFF = 1,             // Apagado
  FLASHING = 2,        // Intermitente
  LOCAL_CONTROL = 3,   // Control local horario
  DEMAND_CONTROL = 4,  // Cambio de planes por demanda
  REMOTE_CONTROL = 6,  // Selección externa (centralizado)
  PHASE_END = 8        // Control por fin de fase
}

export enum StateChangeOrigin {
  ALARM = 'A',
  INITIAL = 'I',
  TIMETABLE = 'H',
  CALENDAR = 'C',
  CALIBRATION = 'S',
  LOCAL_TERMINAL = 'T',
  EXTERNAL = 'E',
  DEMAND = 'K'
}

// =============================================================================
// TRAFFIC LIGHTS (GROUPS)
// =============================================================================

export enum LightColor {
  OFF = 0,
  GREEN = 1,
  AMBER = 2,
  RED = 3
}

export enum LightState {
  OFF = 0,
  ON = 1,
  FLASHING = 2,
  FLASHING_FAST = 3
}

export interface TrafficLightGroup {
  id: number;          // 1-32
  red: LightState;
  amber: LightState;
  green: LightState;
  redError: boolean;
  amberError: boolean;
  greenError: boolean;
}

export interface Position {
  groups: TrafficLightGroup[];
  isStable: boolean;   // false = transitional position
  timestamp: number;
}

// =============================================================================
// PHASES AND PLANS
// =============================================================================

export interface Phase {
  id: string;          // Letter A-Z
  position: Position;
  minTime: number;     // Tenths of second
  maxTime: number;     // Tenths of second
  extensionTime: number;
  isActuated: boolean;
}

export interface Transition {
  fromPhase: string;
  toPhase: string;
  positions: Position[];
  duration: number;    // Tenths of second
}

export enum PlanType {
  FIXED_TIME = 'fixed',
  ACTUATED = 'actuated',
  SEMI_ACTUATED = 'semi_actuated'
}

export interface Plan {
  id: number;          // 0-8 (0 = external plan)
  type: PlanType;
  structure: number;   // 1-8
  phases: Phase[];
  transitions: Transition[];
  cycle: number;       // Tenths of second
  offset: number;      // Tenths of second (desfase)
  isCoordinated: boolean;
  allowManualAdvance: boolean;
}

// =============================================================================
// DETECTORS
// =============================================================================

export enum DetectorType {
  PHYSICAL = 'physical',
  SPEED_DOUBLE = 'speed_double',
  SPEED_SIMPLE = 'speed_simple',
  QUEUE = 'queue',
  PHASE = 'phase',
  GROUP = 'group',
  DETECTOR = 'detector',
  DEMAND = 'demand',
  REMOTE = 'remote'
}

export interface Detector {
  id: number;          // 1-32
  type: DetectorType;
  active: boolean;
  inverted: boolean;
  forced: boolean;
  delay: number;       // Tenths of second
  extension: number;   // Tenths of second
  memory: boolean;     // Memory state
}

export interface DetectorMeasurement {
  detectorId: number;
  intensity: number;      // Vehicles per hour
  occupancy: number;      // Percentage 0-100
  speed?: number;         // km/h (only for speed detectors)
  queue: boolean;
  timestamp: number;
}

export interface Demand {
  id: number;          // 1-32
  direct: boolean;     // From detectors only
  memorized: boolean;  // From detectors + memories
  equation: string;    // Logical expression
}

// =============================================================================
// ALARMS
// =============================================================================

export enum AlarmType {
  TEMPORAL = 'temporal',  // Has start and end
  COUNTING = 'counting'   // Just occurrence count
}

export interface Alarm {
  code: string;        // Single letter or up to 4 chars (extended)
  subcode?: string;    // Subalarm
  type: AlarmType;
  active: boolean;
  activationTime?: number;
  deactivationTime?: number;
  data?: Uint8Array;
  description: string;
}

// Standard alarm codes
export const AlarmCodes = {
  '@': 'Equipment test failure',
  'A': 'Voltage alarm',
  'B': 'Battery alarm',
  'C': 'Color incompatibility',
  'D': 'Demand alarm',
  'E': 'System error',
  'F': 'Phase timeout',
  'G': 'Group substitution',
  'H': 'Time exceeded',
  'I': 'Incompatibility',
  'J': 'Lamp fused',
  'K': 'Local manipulation',
  'L': 'Power fused',
  'M': 'Manual mode',
  'N': 'Temperature alarm',
  'R': 'Reset',
  'S': 'Synchronization',
  'T': 'Communication timeout',
  'V': 'Voltage range',
  'X': 'External alarm'
} as const;

// =============================================================================
// SPONTANEOUS MESSAGES
// =============================================================================

export enum SpontaneousMessageType {
  ALARMS = 0,
  DETECTORS = 1,
  POSITION_CHANGE = 2,
  STATE_CHANGE = 3,
  RESERVED = 4,
  DETECTOR_REALTIME = 5,
  GROUP_CHANGE = 6,
  VARIOUS = 7,
  TRANSPORT_PRIORITY = 8
}

export interface SpontaneousConfig {
  type: SpontaneousMessageType;
  activated: boolean;
  enabled: boolean;
  destination: PhysicalId;
}

// =============================================================================
// TIME FORMATS
// =============================================================================

// Julian date since 1980-01-01
export type JulianDate = number;

// Time in tenths of second since midnight
export type TimeOfDay = number;

export interface DateTime {
  date: JulianDate;
  time: TimeOfDay;
}

// =============================================================================
// REGULATOR COMPLETE STATE
// =============================================================================

export interface RegulatorFullState {
  identification: NodeIdentification;
  state: RegulatorState;
  stateOrigin: StateChangeOrigin;
  currentPlan: Plan;
  currentPhase: Phase;
  currentPosition: Position;
  inTransition: boolean;
  cyclePosition: number;     // Current position in cycle (tenths)
  groups: TrafficLightGroup[];
  detectors: Detector[];
  demands: Demand[];
  activeAlarms: Alarm[];
  spontaneousConfig: SpontaneousConfig[];
  lastUpdate: DateTime;
}
